import { useState, useMemo } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { ChartContainer, ChartTooltip, ChartTooltipContent, ChartLegend, ChartLegendContent } from "@/components/ui/chart";
import { LineChart, Line, XAxis, YAxis, CartesianGrid } from "recharts";
import type { WeaponData } from "@/lib/types/weapon";
import type { AccuracyDataPointsForType } from "@/lib/types/ammo";
import type { Unit } from "@/lib/types/unit";
import type { Veterancy } from "@/lib/types/veterancy-cohesion";
import { useExpertMode } from "@/lib/store/expert-mode-store";
import { WeaponImage } from "@/components/WeaponImage/WeaponImage";
import { useNavigate } from "react-router-dom";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { useDataStore } from "@/lib/store/data-store";
import { FieldDisplay } from "@/components/ui/FieldDisplay";
import { formatDistance } from "@/lib/utils/format-distance";
import { formatTime } from "@/lib/utils/format-time";
import { calculateModifiedWeaponStats } from "@/lib/utils/weapon-stat-modifiers";
import { TraitBadgeList } from "@/components/ui/trait-badge-list";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";

/** Stable ids for `AccordionItem value` — used to sync open state across weapon cards */
export const WEAPON_CARD_ACCORDION_IDS = [
  "damage",
  "basic",
  "range",
  "accuracy",
  "ammo",
  "charts",
] as const;

/** All sections expanded (expert-only items still open when they mount) */
export const WEAPON_CARD_ACCORDION_DEFAULT_OPEN: string[] = [...WEAPON_CARD_ACCORDION_IDS];

interface WeaponCardProps {
  weapon: WeaponData;
  weaponIndex: number;
  traitOverrides?: Map<string, boolean | undefined>;
  veterancy?: Veterancy;
  unit?: Unit;
  /** When set with `onAccordionOpenChange`, accordion state is controlled (e.g. unit page syncs all weapons) */
  accordionOpen?: string[];
  onAccordionOpenChange?: (open: string[]) => void;
}

export function WeaponCard({
  weapon,
  weaponIndex,
  traitOverrides,
  veterancy,
  unit,
  accordionOpen,
  onAccordionOpenChange,
}: WeaponCardProps) {
  const { ammo, numberOfWeapons, hasTurret, turretRotationSpeed, missile, ammoDescriptors } = weapon;
  const { isExpertMode } = useExpertMode();
  const navigate = useNavigate();
  const { getAmmoByDescriptor } = useDataStore();
  const [dialogOpen, setDialogOpen] = useState(false);

  // Calculate modified weapon stats
  const modifiedStats = useMemo(() => {
    if (!unit || !traitOverrides) return undefined;
    try {
      return calculateModifiedWeaponStats(weapon, unit, traitOverrides, veterancy);
    } catch {
      // Trait modifiers map not initialized yet
      return undefined;
    }
  }, [weapon, unit, traitOverrides, veterancy]);

  // Some weapons are "merged" from multiple ammo descriptors.
  // To avoid missing traits (e.g. KE/HEAT variants), union traits across all descriptor ammos.
  const weaponTraits = useMemo(() => {
    const traits: string[] = [];
    const seen = new Set<string>();

    const pushTrait = (trait: string) => {
      const trimmed = trait?.trim();
      if (!trimmed) return;
      if (seen.has(trimmed)) return;
      seen.add(trimmed);
      traits.push(trimmed);
    };

    if (ammoDescriptors && ammoDescriptors.length > 0) {
      ammoDescriptors.forEach((descriptorName) => {
        const descriptorAmmo = getAmmoByDescriptor(descriptorName);
        descriptorAmmo?.traits?.forEach(pushTrait);
      });
    }

    // Fallback: if descriptors are missing/empty, still show whatever the merged ammo provides.
    if (traits.length === 0) {
      ammo.traits?.forEach(pushTrait);
    }

    return traits;
  }, [ammo, ammoDescriptors, getAmmoByDescriptor]);

  const handleCardClick = () => {
    // Check if weapon has multiple ammo descriptors
    if (ammoDescriptors && ammoDescriptors.length > 1) {
      // Show dialog to select which descriptor to view
      setDialogOpen(true);
    } else {
      // Navigate directly to the single descriptor (or default to ammo.descriptorName)
      let descriptorName: string;
      if (ammoDescriptors && ammoDescriptors.length === 1 && ammoDescriptors[0]) {
        descriptorName = ammoDescriptors[0];
      } else {
        descriptorName = ammo.descriptorName;
      }
      navigate(`/weapons/${encodeURIComponent(descriptorName)}`);
    }
  };

  const handleDescriptorSelect = (descriptorName: string) => {
    setDialogOpen(false);
    navigate(`/weapons/${encodeURIComponent(descriptorName)}`);
  };

  // Prepare accuracy data for charts - combine all series into one data array
  // Each series will have its own data points, with null for other series at those distances
  const prepareAccuracyData = (accuracyData?: AccuracyDataPointsForType) => {
    if (!accuracyData) return [];

    // Collect all unique distances from all series
    const allDistances = new Set<number>();
    if (accuracyData.ground) {
      accuracyData.ground.forEach((p) => allDistances.add(p.distance));
    }
    if (accuracyData.helicopter) {
      accuracyData.helicopter.forEach((p) => allDistances.add(p.distance));
    }
    if (accuracyData.plane) {
      accuracyData.plane.forEach((p) => allDistances.add(p.distance));
    }

    const sortedDistances = Array.from(allDistances).sort((a, b) => a - b);

    // Create data points with all series, using actual values or null
    const data = sortedDistances.map((dist) => {
      const point: { distance: number; Ground?: number; Heli?: number; Plane?: number } = {
        distance: dist,
      };

      if (accuracyData.ground) {
        const groundPoint = accuracyData.ground.find((p) => p.distance === dist);
        if (groundPoint) {
          point.Ground = groundPoint.accuracy;
        }
      }

      if (accuracyData.helicopter) {
        const heliPoint = accuracyData.helicopter.find((p) => p.distance === dist);
        if (heliPoint) {
          point.Heli = heliPoint.accuracy;
        }
      }

      if (accuracyData.plane) {
        const planePoint = accuracyData.plane.find((p) => p.distance === dist);
        if (planePoint) {
          point.Plane = planePoint.accuracy;
        }
      }

      return point;
    });

    return data;
  };

  const staticAccuracyData = prepareAccuracyData(ammo.staticAccuracyOverDistance);
  const movingAccuracyData = prepareAccuracyData(ammo.movingAccuracyOverDistance);

  // Get computed chart colors - CSS variables need to be resolved for SVG
  const getChartColor = (varName: string): string => {
    if (typeof window === 'undefined') return 'oklch(0.488 0.243 264.376)';
    const root = document.documentElement;
    const value = getComputedStyle(root).getPropertyValue(varName).trim();
    return value || 'oklch(0.488 0.243 264.376)';
  };

  const chartConfig = {
    Ground: {
      label: "Ground",
      color: getChartColor('--chart-1'),
    },
    Heli: {
      label: "Helicopter",
      color: getChartColor('--chart-2'),
    },
    Plane: {
      label: "Plane",
      color: getChartColor('--chart-3'),
    },
  };

  // Calculate ammunition totals
  const totalAmmunition = ammo.ammunitionPerSalvo * weapon.numberOfSalvos;
  const trueAmmunition = ammo.trueRateOfFire > 0 
    ? Math.floor((ammo.trueRateOfFire / ammo.rateOfFire) * totalAmmunition)
    : totalAmmunition;

  const showExpertBasic = isExpertMode && (hasTurret || !!missile);

  const accordionItemClass = "border-0 border-t border-border";

  const accordionTriggerClass =
    "py-2 text-xs font-semibold uppercase tracking-wide text-muted-foreground hover:no-underline";

  const accordionControlled =
    accordionOpen !== undefined && onAccordionOpenChange !== undefined;

  return (
    <Card>
      <CardHeader className="p-2 pb-1">
        <CardTitle 
          className="flex items-center justify-between text-base cursor-pointer hover:text-primary transition-colors"
          onClick={handleCardClick}
        >
          <span className="truncate">{ammo.name} x {numberOfWeapons}</span>
          {ammo.textureId && (
            <div className="flex-shrink-0 ml-2">
              <WeaponImage
                textureId={ammo.textureId}
                alt={ammo.name}
                size="medium"
              />
            </div>
          )}
        </CardTitle>
      </CardHeader>
      <CardContent className="p-2 pt-1 space-y-1">
        {/* Traits section - up-front so KE/HEAT isn't hidden behind the ammo click-through */}
        {weaponTraits.length > 0 && (
          <div className="space-y-1 border-t pt-1">
            <TraitBadgeList specialities={weaponTraits} size="sm" />
          </div>
        )}

        <Accordion
          type="multiple"
          className="w-full"
          {...(accordionControlled
            ? { value: accordionOpen, onValueChange: onAccordionOpenChange }
            : { defaultValue: WEAPON_CARD_ACCORDION_DEFAULT_OPEN })}
        >
          <AccordionItem value="damage" className={accordionItemClass}>
            <AccordionTrigger className={accordionTriggerClass}>Damage</AccordionTrigger>
            <AccordionContent className="pb-2 pt-0">
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-1">
                  <FieldDisplay fieldKey="penetration" value={ammo.penetration} fieldType="weapon-ammo" />
                  <div className="flex flex-col gap-0">
                    <span className="text-xs text-muted-foreground">HE</span>
                    <span className={`text-sm font-semibold ${modifiedStats?.weaponDamage?.isModified ? "text-primary" : ""}`}>
                      {modifiedStats?.weaponDamage
                        ? (modifiedStats.weaponDamage.modified * numberOfWeapons).toFixed(3)
                        : weapon.totalHeDamage.toFixed(3)}
                      {modifiedStats?.weaponDamage?.isModified && (
                        <span className="text-xs text-muted-foreground ml-1">
                          ({weapon.totalHeDamage.toFixed(3)})
                        </span>
                      )}
                    </span>
                  </div>
                  {isExpertMode && (
                    <div className="flex flex-col gap-0">
                      <span className="text-xs text-muted-foreground">HE per Weapon</span>
                      <span className={`text-sm font-semibold ${modifiedStats?.weaponDamage?.isModified ? "text-primary" : ""}`}>
                        {(modifiedStats?.weaponDamage?.modified ?? ammo.heDamage).toFixed(3)}
                        {modifiedStats?.weaponDamage?.isModified && (
                          <span className="text-xs text-muted-foreground ml-1">
                            ({modifiedStats.weaponDamage.base.toFixed(3)})
                          </span>
                        )}
                      </span>
                    </div>
                  )}
                </div>
                <div className="space-y-1">
                  <FieldDisplay fieldKey="suppress" value={ammo.suppress} fieldType="weapon-ammo" />
                  {isExpertMode && (
                    <>
                      <div className="flex flex-col gap-0">
                        <span className="text-xs text-muted-foreground">HE per Weapon</span>
                        <span className="text-sm font-semibold">{ammo.heDamage.toFixed(3)}</span>
                      </div>
                      <FieldDisplay fieldKey="heDamageRadius" value={ammo.heDamageRadius} fieldType="weapon-ammo" />
                      <FieldDisplay fieldKey="suppressDamagesRadius" value={ammo.suppressDamagesRadius} fieldType="weapon-ammo" />
                    </>
                  )}
                </div>
              </div>
            </AccordionContent>
          </AccordionItem>

          {showExpertBasic && (
            <AccordionItem value="basic" className={accordionItemClass}>
              <AccordionTrigger className={accordionTriggerClass}>Basic</AccordionTrigger>
              <AccordionContent className="pb-2 pt-0">
                <div className="space-y-1">
                  {hasTurret && (
                    <FieldDisplay fieldKey="turretRotationSpeed" value={turretRotationSpeed} fieldType="weapon-weapon" />
                  )}
                  {missile && (
                    <>
                      <div className="flex flex-col gap-0">
                        <span className="text-xs text-muted-foreground">Missile Speed</span>
                        <span className="text-sm font-semibold">{missile.maxMissileSpeed} m/s</span>
                      </div>
                      <div className="flex flex-col gap-0">
                        <span className="text-xs text-muted-foreground">Missile Acceleration</span>
                        <span className="text-sm font-semibold">{missile.maxMissileAcceleration} m/s²</span>
                      </div>
                    </>
                  )}
                </div>
              </AccordionContent>
            </AccordionItem>
          )}

          <AccordionItem value="range" className={accordionItemClass}>
            <AccordionTrigger className={accordionTriggerClass}>Range</AccordionTrigger>
            <AccordionContent className="pb-2 pt-0">
              <div className="grid grid-cols-2 gap-1">
                <div className="space-y-1">
                  {ammo.groundMaxRange > 0 && (
                    <div className="flex flex-col gap-0">
                      <span className="text-xs text-muted-foreground">Ground</span>
                      <span className="text-sm font-semibold">
                        {formatDistance(ammo.groundMaxRange)}
                        {ammo.groundMinRange > 0 ? `/${formatDistance(ammo.groundMinRange)}` : ""}
                      </span>
                    </div>
                  )}
                  {ammo.heliMaxRange > 0 && (
                    <div className="flex flex-col gap-0">
                      <span className="text-xs text-muted-foreground">Helicopter</span>
                      <span className="text-sm font-semibold">
                        {formatDistance(ammo.heliMaxRange)}
                        {ammo.heliMinRange > 0 ? `/${formatDistance(ammo.heliMinRange)}` : ""}
                      </span>
                    </div>
                  )}
                  {ammo.planeMaxRange > 0 && (
                    <div className="flex flex-col gap-0">
                      <span className="text-xs text-muted-foreground">Aircraft</span>
                      <span className="text-sm font-semibold">
                        {formatDistance(ammo.planeMaxRange)}
                        {ammo.planeMinRange > 0 ? `/${formatDistance(ammo.planeMinRange)}` : ""}
                      </span>
                    </div>
                  )}
                </div>
                {isExpertMode && (
                  <div className="space-y-1">
                    <FieldDisplay fieldKey="dispersionAtMaxRange" value={ammo.dispersionAtMaxRange} fieldType="weapon-ammo" />
                    <FieldDisplay fieldKey="dispersionAtMinRange" value={ammo.dispersionAtMinRange} fieldType="weapon-ammo" />
                  </div>
                )}
              </div>
            </AccordionContent>
          </AccordionItem>

          <AccordionItem value="accuracy" className={accordionItemClass}>
            <AccordionTrigger className={accordionTriggerClass}>Accuracy per Shot</AccordionTrigger>
            <AccordionContent className="pb-2 pt-0">
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-1">
                  <div className="flex flex-col gap-0">
                    <span className="text-xs text-muted-foreground">Static</span>
                    <span className={`text-sm font-semibold ${modifiedStats?.staticAccuracy.isModified ? "text-primary" : ""}`}>
                      {modifiedStats?.staticAccuracy.modified ?? ammo.staticAccuracy}%
                      {modifiedStats?.staticAccuracy.isModified && (
                        <span className="text-xs text-muted-foreground ml-1">
                          ({modifiedStats.staticAccuracy.base}%)
                        </span>
                      )}
                    </span>
                  </div>
                  {isExpertMode && (
                    <FieldDisplay fieldKey="staticPrecisionBonusPerShot" value={ammo.staticPrecisionBonusPerShot} fieldType="weapon-ammo" />
                  )}
                </div>
                <div className="space-y-1">
                  <div className="flex flex-col gap-0">
                    <span className="text-xs text-muted-foreground">Motion</span>
                    <span className={`text-sm font-semibold ${modifiedStats?.movingAccuracy.isModified ? "text-primary" : ""}`}>
                      {modifiedStats?.movingAccuracy.modified !== undefined
                        ? `${modifiedStats.movingAccuracy.modified}%`
                        : ammo.movingAccuracy !== undefined
                          ? `${ammo.movingAccuracy}%`
                          : "-"}
                      {modifiedStats?.movingAccuracy.isModified && modifiedStats.movingAccuracy.base !== undefined && (
                        <span className="text-xs text-muted-foreground ml-1">
                          ({modifiedStats.movingAccuracy.base}%)
                        </span>
                      )}
                    </span>
                  </div>
                  {isExpertMode && (
                    <>
                      <FieldDisplay fieldKey="movingPrecisionBonusPerShot" value={ammo.movingPrecisionBonusPerShot} fieldType="weapon-ammo" />
                      <FieldDisplay fieldKey="maxSuccessiveHitCount" value={ammo.maxSuccessiveHitCount} fieldType="weapon-ammo" />
                    </>
                  )}
                </div>
              </div>
            </AccordionContent>
          </AccordionItem>

          <AccordionItem value="ammo" className={accordionItemClass}>
            <AccordionTrigger className={accordionTriggerClass}>Ammo</AccordionTrigger>
            <AccordionContent className="pb-2 pt-0">
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-1">
                  <FieldDisplay fieldKey="rateOfFire" value={ammo.rateOfFire} fieldType="weapon-ammo" />
                  {isExpertMode && (
                    <FieldDisplay fieldKey="trueRateOfFire" value={ammo.trueRateOfFire} fieldType="weapon-ammo" />
                  )}
                  <div className="flex flex-col gap-0">
                    <span className="text-xs text-muted-foreground">Aiming Time</span>
                    <span className={`text-sm font-semibold ${modifiedStats?.aimingTime.isModified ? "text-primary" : ""}`}>
                      {formatTime(modifiedStats?.aimingTime.modified ?? ammo.aimingTime)}
                      {modifiedStats?.aimingTime.isModified && (
                        <span className="text-xs text-muted-foreground ml-1">
                          ({formatTime(modifiedStats.aimingTime.base)})
                        </span>
                      )}
                    </span>
                  </div>
                  <div className="flex flex-col gap-0">
                    <span className="text-xs text-muted-foreground">Reload Time</span>
                    <span className={`text-sm font-semibold ${modifiedStats?.reloadTime.isModified ? "text-primary" : ""}`}>
                      {formatTime(modifiedStats?.reloadTime.modified ?? ammo.reloadTime)}
                      {modifiedStats?.reloadTime.isModified && (
                        <span className="text-xs text-muted-foreground ml-1">
                          ({formatTime(modifiedStats.reloadTime.base)})
                        </span>
                      )}
                    </span>
                  </div>
                  {modifiedStats?.timeBetweenShots && modifiedStats.timeBetweenShots.isModified && (
                    <div className="flex flex-col gap-0">
                      <span className="text-xs text-muted-foreground">Time Between Shots</span>
                      <span className="text-sm font-semibold text-primary">
                        {formatTime(modifiedStats.timeBetweenShots.modified)}
                        <span className="text-xs text-muted-foreground ml-1">
                          ({formatTime(modifiedStats.timeBetweenShots.base)})
                        </span>
                      </span>
                    </div>
                  )}
                </div>
                <div className="space-y-1">
                  <div className="flex flex-col gap-0">
                    <span className="text-xs text-muted-foreground">Ammunition</span>
                    <span className="text-sm font-semibold">{totalAmmunition}</span>
                  </div>
                  {isExpertMode && (
                    <div className="flex flex-col gap-0">
                      <span className="text-xs text-muted-foreground">True Ammunition</span>
                      <span className="text-sm font-semibold">{trueAmmunition}</span>
                    </div>
                  )}
                  <FieldDisplay fieldKey="salvoLength" value={ammo.salvoLength} fieldType="weapon-ammo" />
                  {isExpertMode && (
                    <>
                      <FieldDisplay fieldKey="timeBetweenSalvos" value={ammo.timeBetweenSalvos} fieldType="weapon-ammo" />
                      <FieldDisplay fieldKey="noiseMalus" value={ammo.noiseMalus} fieldType="weapon-ammo" />
                      <FieldDisplay fieldKey="shotsBeforeMaxNoise" value={ammo.shotsBeforeMaxNoise} fieldType="weapon-ammo" />
                    </>
                  )}
                  <FieldDisplay fieldKey="supplyCost" value={weapon.supplyCost} fieldType="weapon-weapon" />
                </div>
              </div>
            </AccordionContent>
          </AccordionItem>

          {isExpertMode && (
            <AccordionItem value="charts" className={accordionItemClass}>
              <AccordionTrigger className={accordionTriggerClass}>Accuracy over distance</AccordionTrigger>
              <AccordionContent className="pb-2 pt-0">
                <div className="-mx-2 space-y-3 px-2">
                  <div>
                    <div className="mb-1 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                      Static
                    </div>
                    {staticAccuracyData.length > 0 ? (
                      <ChartContainer config={chartConfig} className="h-[150px] w-full">
                        <LineChart data={staticAccuracyData} width={undefined} height={undefined}>
                          <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--muted-foreground) / 0.3)" />
                          <XAxis
                            dataKey="distance"
                            tick={{ fontSize: 11 }}
                            label={{ value: "Range (m)", position: "insideBottom", offset: -5, style: { fontSize: 11 } }}
                          />
                          <YAxis
                            tick={{ fontSize: 11 }}
                            label={{ value: "Accuracy (%)", angle: -90, position: "insideLeft", style: { fontSize: 11 } }}
                            domain={[0, 100]}
                          />
                          <ChartTooltip content={<ChartTooltipContent />} />
                          <ChartLegend content={<ChartLegendContent />} />
                          {ammo.staticAccuracyOverDistance?.ground && (
                            <Line
                              type="monotone"
                              dataKey="Ground"
                              stroke={chartConfig.Ground.color}
                              strokeWidth={3}
                              dot={false}
                              activeDot={{ r: 4 }}
                              connectNulls={true}
                            />
                          )}
                          {ammo.staticAccuracyOverDistance?.helicopter && (
                            <Line
                              type="monotone"
                              dataKey="Heli"
                              stroke={chartConfig.Heli.color}
                              strokeWidth={3}
                              dot={false}
                              activeDot={{ r: 4 }}
                              connectNulls={true}
                            />
                          )}
                          {ammo.staticAccuracyOverDistance?.plane && (
                            <Line
                              type="monotone"
                              dataKey="Plane"
                              stroke={chartConfig.Plane.color}
                              strokeWidth={3}
                              dot={false}
                              activeDot={{ r: 4 }}
                              connectNulls={true}
                            />
                          )}
                        </LineChart>
                      </ChartContainer>
                    ) : (
                      <div className="flex h-[150px] w-full items-center justify-center rounded border border-dashed text-sm text-muted-foreground">
                        No static accuracy data
                      </div>
                    )}
                  </div>
                  <div>
                    <div className="mb-1 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                      Moving
                    </div>
                    {movingAccuracyData.length > 0 ? (
                      <ChartContainer config={chartConfig} className="h-[150px] w-full">
                        <LineChart data={movingAccuracyData} width={undefined} height={undefined}>
                          <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--muted-foreground) / 0.3)" />
                          <XAxis
                            dataKey="distance"
                            tick={{ fontSize: 11 }}
                            label={{ value: "Range (m)", position: "insideBottom", offset: -5, style: { fontSize: 11 } }}
                          />
                          <YAxis
                            tick={{ fontSize: 11 }}
                            label={{ value: "Accuracy (%)", angle: -90, position: "insideLeft", style: { fontSize: 11 } }}
                            domain={[0, 100]}
                          />
                          <ChartTooltip content={<ChartTooltipContent />} />
                          <ChartLegend content={<ChartLegendContent />} />
                          {ammo.movingAccuracyOverDistance?.ground && (
                            <Line
                              type="monotone"
                              dataKey="Ground"
                              stroke={chartConfig.Ground.color}
                              strokeWidth={3}
                              dot={false}
                              activeDot={{ r: 4 }}
                              connectNulls={true}
                            />
                          )}
                          {ammo.movingAccuracyOverDistance?.helicopter && (
                            <Line
                              type="monotone"
                              dataKey="Heli"
                              stroke={chartConfig.Heli.color}
                              strokeWidth={3}
                              dot={false}
                              activeDot={{ r: 4 }}
                              connectNulls={true}
                            />
                          )}
                          {ammo.movingAccuracyOverDistance?.plane && (
                            <Line
                              type="monotone"
                              dataKey="Plane"
                              stroke={chartConfig.Plane.color}
                              strokeWidth={3}
                              dot={false}
                              activeDot={{ r: 4 }}
                              connectNulls={true}
                            />
                          )}
                        </LineChart>
                      </ChartContainer>
                    ) : (
                      <div className="flex h-[150px] w-full items-center justify-center rounded border border-dashed text-sm text-muted-foreground">
                        No moving accuracy data
                      </div>
                    )}
                  </div>
                </div>
              </AccordionContent>
            </AccordionItem>
          )}
        </Accordion>
      </CardContent>

      {/* Descriptor Selection Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Select Ammo Descriptor</DialogTitle>
            <DialogDescription>
              This weapon has multiple ammo descriptors. Please select which one you want to view.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-2 mt-4">
            {ammoDescriptors?.map((descriptorName) => {
              const descriptorAmmo = getAmmoByDescriptor(descriptorName);
              const displayName = descriptorAmmo?.name || descriptorName;
              return (
                <Button
                  key={descriptorName}
                  variant="outline"
                  className="w-full justify-start text-left h-auto py-3"
                  onClick={() => handleDescriptorSelect(descriptorName)}
                >
                  <div className="flex flex-col items-start gap-1">
                    <span className="font-semibold">{displayName}</span>
                    <span className="text-xs text-muted-foreground font-mono">{descriptorName}</span>
                  </div>
                </Button>
              );
            })}
          </div>
        </DialogContent>
      </Dialog>
    </Card>
  );
}


